// Percorsi. Modalità "hash" (#/…): nessuna regola speciale in nginx, e i link delle email
// del backend puntano a /#/password?token=…
import { createRouter, createWebHashHistory } from 'vue-router';
import { impostaSuSessioneScaduta } from './api/client';
import { segnalaErrore } from './errori';
import { useSessione } from './stores/sessione';
import { avviaControlloVersione } from './versione';

export const router = createRouter({
  history: createWebHashHistory(),
  routes: [
    { path: '/login', component: () => import('./pagine/PaginaLogin.vue'), meta: { pubblica: true } },
    { path: '/password', component: () => import('./pagine/PaginaPassword.vue'), meta: { pubblica: true } },
    {
      path: '/',
      component: () => import('./layout/LayoutPrincipale.vue'),
      children: [
        { path: '', component: () => import('./pagine/PaginaOggi.vue') },
        { path: 'clienti', component: () => import('./pagine/PaginaClienti.vue') },
        { path: 'clienti/:uuid', component: () => import('./pagine/PaginaScheda.vue'), props: true },
        { path: 'mappa', component: () => import('./pagine/PaginaMappa.vue') },
        { path: 'giro', component: () => import('./pagine/PaginaGiro.vue') },
        // Cruscotto per la direzione: scaduto (D35) e attività per utente (D38), con ?vista=attivita
        { path: 'cruscotto', component: () => import('./pagine/PaginaCruscotto.vue') },
        { path: 'scaduto', redirect: '/cruscotto' },
        // Segnalazioni alla sede (D41): coda dell'ufficio e segnalazioni proprie
        { path: 'segnalazioni', component: () => import('./pagine/PaginaSegnalazioni.vue') },
        // Cataloghi da inviare ai clienti (D49): la gestione è di UFFICIO e DIREZIONE
        { path: 'cataloghi', component: () => import('./pagine/PaginaCataloghi.vue'), meta: { soloGestioneCataloghi: true } },
        { path: 'utenti', component: () => import('./pagine/PaginaUtenti.vue'), meta: { soloAmministratori: true } },
        { path: 'ditta', component: () => import('./pagine/PaginaSceltaDitta.vue'), meta: { senzaDitta: true } },
      ],
    },
    { path: '/:altro(.*)*', redirect: '/' },
  ],
});

router.beforeEach(async (to) => {
  const s = useSessione();
  if (!s.caricata) await s.carica();
  if (to.meta.pubblica) return to.path === '/login' && s.sessione ? '/' : true;
  if (!s.sessione) return { path: '/login', query: to.fullPath !== '/' ? { ritorno: to.fullPath } : {} };
  if (!s.dittaAttiva && !to.meta.senzaDitta) return '/ditta';
  if (to.meta.soloAmministratori && !s.utente?.amministratore) return '/';
  if (to.meta.soloGestioneCataloghi && !s.gestisceCataloghi) return '/';
  return true;
});

// Dopo una pubblicazione i file delle pagine cambiano nome. Se il browser ha ancora l'app vecchia e una
// pagina non si carica, si ricarica tutto andando sulla pagina chiesta, una volta sola per non girare in
// tondo se il problema è un altro (02/10/2026; anche nginx e 06-installa-frontend.sh, docs/02).
function ricaricaUnaVolta(percorso: string): boolean {
  try {
    const ultima = Number(sessionStorage.getItem('crm-ricarica') ?? 0);
    if (Date.now() - ultima < 30_000) return false;
    sessionStorage.setItem('crm-ricarica', String(Date.now()));
  } catch {
    return false;
  }
  window.location.hash = `#${percorso}`;
  window.location.reload();
  return true;
}
window.addEventListener('vite:preloadError', (e) => {
  if (ricaricaUnaVolta(router.currentRoute.value.fullPath)) e.preventDefault();
});
router.onError((err: unknown, to) => {
  const msg = err instanceof Error ? err.message : String(err);
  if (/dynamically imported module|Importing a module script failed|error loading dynamically imported/i.test(msg)) ricaricaUnaVolta(to.fullPath);
  else segnalaErrore('router', err); // gli altri errori di navigazione al backend (D61)
});

// Versione nuova pubblicata: avviso nel layout (src/versione.ts)
avviaControlloVersione((f) => router.afterEach(f));

// Sessione scaduta durante il lavoro: si torna al login, conservando la pagina
impostaSuSessioneScaduta(() => {
  useSessione().dimentica();
  const attuale = router.currentRoute.value;
  if (!attuale.meta.pubblica) void router.push({ path: '/login', query: { ritorno: attuale.fullPath, scaduta: '1' } });
});
